<template>
  <div class="solution-detail-page">
    <!-- ========== 顶部导航 ========== -->
    <AppHeader />

    <!-- ========== 加载状态 ========== -->
    <div v-if="loading" class="loading-state">
      <el-icon class="spin" :size="40"><Loading /></el-icon>
      <p>加载方案详情...</p>
    </div>

    <!-- ========== 详情内容 ========== -->
    <template v-else-if="solution">
      <!-- 英雄区 - 方案概览 -->
      <section class="detail-hero">
        <div class="hero-backdrop">
          <!-- <el-image :src="solution.banner || solution.image" fit="cover" class="hero-bg-image">
            <template #error>
              <div class="hero-bg-fallback"></div>
            </template>
          </el-image> -->
          <div class="hero-overlay"></div>
        </div>
        <div class="hero-content">
          <div class="hero-breadcrumb">
            <el-breadcrumb separator="/">
              <el-breadcrumb-item :to="{ path: '/solutions' }">解决方案</el-breadcrumb-item>
              <el-breadcrumb-item>{{ solution.industryName || solution.industry || '行业' }}</el-breadcrumb-item>
              <el-breadcrumb-item>{{ solution.name }}</el-breadcrumb-item>
            </el-breadcrumb>
          </div>
          <div class="hero-main">
            <div class="hero-info">
              <div class="hero-tags">
                <span class="hero-tag" v-for="tag in solution.tags" :key="tag">{{ tag }}</span>
                <span class="hero-tag primary" v-if="solution.tag">{{ solution.tag }}</span>
              </div>
              <h1 class="hero-title">{{ solution.name }}</h1>
              <p class="hero-desc">{{ solution.description || solution.desc }}</p>
              <div class="hero-meta">
                <div class="meta-item">
                  <el-icon><Monitor /></el-icon>
                  <span>{{ solution.industryName || solution.industry || '行业' }}</span>
                </div>
                <div class="meta-divider"></div>
                <div class="meta-item">
                  <el-icon><Calendar /></el-icon>
                  <span>更新于 {{ formatDate(solution.updateTime || solution.updatedAt, '--') }}</span>
                </div>
                <div class="meta-divider"></div>
                <div class="meta-item">
                  <el-icon><View /></el-icon>
                  <span>{{ solution.views || 0 }} 次浏览</span>
                </div>
              </div>
              <div class="hero-actions">
                <el-button type="primary" size="large" round class="action-btn" @click="handleConsult">
                  获取方案
                  <el-icon><ArrowRight /></el-icon>
                </el-button>
                <el-button size="large" round class="action-btn-ghost" @click="handleConsult">
                  预约咨询
                </el-button>
              </div>
            </div>
            <div class="hero-stats-card" v-if="solution.efficiency || solution.costReduction || solution.deploymentTime">
              <div class="stat-item" v-if="solution.efficiency">
                <span class="stat-number">{{ solution.efficiency }}</span>
                <span class="stat-label">效率提升</span>
              </div>
              <div class="stat-divider" v-if="solution.efficiency && solution.costReduction"></div>
              <div class="stat-item" v-if="solution.costReduction">
                <span class="stat-number">{{ solution.costReduction }}</span>
                <span class="stat-label">成本降低</span>
              </div>
              <div class="stat-divider" v-if="solution.costReduction && solution.deploymentTime"></div>
              <div class="stat-item" v-if="solution.deploymentTime">
                <span class="stat-number">{{ solution.deploymentTime }}</span>
                <span class="stat-label">部署周期</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 主体内容 -->
      <section class="detail-body">
        <div class="body-inner">
          <div class="body-layout">
            <!-- 侧边锚点导航 -->


            <!-- 右侧内容区（左右两列） -->
            <div class="detail-content">
              <div class="content-wrapper">
                <!-- 左列：主内容 -->
                <div class="main-content">
                  <!-- 方案概述 -->
                  <section id="overview" class="content-section">
                    <h2 class="section-title">方案概述</h2>
                    <div class="section-body">
                      <div class="markdown-preview">
                        <MarkdownEditor
                          v-model="solution.content"
                          :preview="true"
                          height="auto"
                          upload-module="solution"
                        />
                      </div>
                      <div class="overview-grid" v-if="solution.highlights && solution.highlights.length">
                        <div class="overview-card" v-for="(point, i) in solution.highlights" :key="i">
                          <div class="ov-icon">
                            <el-icon :size="24"><Check /></el-icon>
                          </div>
                          <div class="ov-content">
                            <h4>{{ point.title }}</h4>
                            <p>{{ point.desc }}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>

                  <!-- 技术架构 -->
                  <section id="architecture" class="content-section" v-if="solution.architecture || solution.architectureDesc">
                    <h2 class="section-title">技术架构</h2>
                    <div class="section-body">
                      <p class="section-sub-desc" v-if="solution.architectureDesc">{{ solution.architectureDesc }}</p>
                      <div v-if="solution.architecture" class="architecture-wrapper">
                        <MarkdownEditor
                          v-model="solution.architecture"
                          :preview="true"
                          height="auto"
                          upload-module="solution"
                        />
                      </div>
                      <div v-else-if="solution.applications || solution.platforms || solution.edgeNodes || solution.devices" class="architecture-diagram">
                        <div class="arch-layer arch-layer-1" v-if="solution.applications && solution.applications.length">
                          <span class="layer-label">应用层</span>
                          <div class="layer-items">
                            <span class="layer-item" v-for="app in solution.applications" :key="app">{{ app }}</span>
                          </div>
                        </div>
                        <div class="arch-arrow" v-if="solution.applications && solution.applications.length">↓</div>
                        <div class="arch-layer arch-layer-2" v-if="solution.platforms && solution.platforms.length">
                          <span class="layer-label">平台层</span>
                          <div class="layer-items">
                            <span class="layer-item" v-for="platform in solution.platforms" :key="platform">{{ platform }}</span>
                          </div>
                        </div>
                        <div class="arch-arrow" v-if="solution.platforms && solution.platforms.length">↓</div>
                        <div class="arch-layer arch-layer-3" v-if="solution.edgeNodes && solution.edgeNodes.length">
                          <span class="layer-label">边缘层</span>
                          <div class="layer-items">
                            <span class="layer-item" v-for="edge in solution.edgeNodes" :key="edge">{{ edge }}</span>
                          </div>
                        </div>
                        <div class="arch-arrow" v-if="solution.edgeNodes && solution.edgeNodes.length">↓</div>
                        <div class="arch-layer arch-layer-4" v-if="solution.devices && solution.devices.length">
                          <span class="layer-label">设备层</span>
                          <div class="layer-items">
                            <span class="layer-item" v-for="device in solution.devices" :key="device">{{ device }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>

                  <!-- 核心功能 -->
                  <section id="features" class="content-section" v-if="solution.features && solution.features.length">
                    <h2 class="section-title">核心功能</h2>
                    <div class="section-body">
                      <div class="features-grid">
                        <div class="feature-card" v-for="(feature, i) in solution.features" :key="i">
                          <div class="feature-icon" :style="{ background: feature.color || '#4F46E5' }">
                            <el-icon :size="28"><component :is="feature.icon || 'Monitor'" /></el-icon>
                          </div>
                          <h4>{{ feature.title }}</h4>
                          <p>{{ feature.desc }}</p>
                        </div>
                      </div>
                    </div>
                  </section>

                  <!-- 应用场景 -->
                  <section id="scenarios" class="content-section" v-if="solution.scenarios && solution.scenarios.length">
                    <h2 class="section-title">应用场景</h2>
                    <div class="section-body">
                      <div class="scenarios-grid">
                        <div class="scenario-card" v-for="(scenario, i) in solution.scenarios" :key="i">
                          <div class="scenario-image">
                            <el-image :src="scenario.image" fit="cover">
                              <template #placeholder>
                                <div class="scenario-placeholder">
                                  <el-icon :size="32"><Picture /></el-icon>
                                </div>
                              </template>
                            </el-image>
                          </div>
                          <div class="scenario-info">
                            <h4>{{ scenario.title }}</h4>
                            <p>{{ scenario.desc }}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>

                  <!-- 客户案例 -->
                  <section id="cases" class="content-section" v-if="solution.cases && solution.cases.length">
                    <h2 class="section-title">客户案例</h2>
                    <div class="section-body">
                      <div class="cases-grid">
                        <div class="case-card" v-for="(caseItem, i) in solution.cases" :key="i">
                          <div class="case-avatar">
                            <el-image :src="caseItem.avatar" fit="cover">
                              <template #placeholder>
                                <div class="avatar-placeholder">
                                  <el-icon :size="24"><User /></el-icon>
                                </div>
                              </template>
                            </el-image>
                          </div>
                          <div class="case-info">
                            <h4>{{ caseItem.company }}</h4>
                            <p>{{ caseItem.testimonial }}</p>
                            <span class="case-result">{{ caseItem.result }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>
                </div>

                <!-- 右列：相关方案侧边栏 -->
                <aside class="related-sidebar" v-if="relatedSolutions && relatedSolutions.length">
                  <section id="related" class="content-section">
                    <h2 class="section-title">相关方案</h2>
                    <div class="section-body">
                      <div class="related-grid">
                        <div
                          class="related-card"
                          v-for="rel in relatedSolutions"
                          :key="rel.id"
                          @click="goToDetail(rel.id)"
                        >
                          <el-image :src="rel.image" fit="cover" class="related-image">
                            <template #placeholder>
                              <div class="related-placeholder">
                                <el-icon :size="24"><Monitor /></el-icon>
                              </div>
                            </template>
                          </el-image>
                          <div class="related-info">
                            <h4>{{ rel.name }}</h4>
                            <span>{{ rel.industryName || rel.industry }}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>
                </aside>
              </div>
            </div>
          </div>
        </div>
      </section>
    </template>

    <!-- ========== 404 ========== -->
    <div v-else class="not-found">
      <el-icon :size="80"><Document /></el-icon>
      <h2>方案未找到</h2>
      <p>请返回方案列表查看其他方案</p>
      <el-button type="primary" @click="router.push('/solutions')">返回列表</el-button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import {
  Monitor, Connection, ArrowRight, Loading, Check, Cpu,
  Calendar, View, ChatDotRound, Picture, User, Document,
  DataAnalysis, Grid, Setting, TrendCharts, Promotion
} from '@element-plus/icons-vue'
import AppHeader from '@/components/AppHeader.vue'
import MarkdownEditor from '@/components/MarkdownEditor.vue'
// 
import { getSolutionById } from '@/api/solution'
import { formatDate } from '@/utils/dateFormat'

const route = useRoute()
const router = useRouter()


// ========== 状态 ==========
const loading = ref(true)
const solution = ref(null)
const activeSection = ref('overview')
const relatedSolutions = ref([])

// ========== 导航目录（动态生成） ==========
const navSections = computed(() => {
  const sections = [{ id: 'overview', label: '方案概述' }]
  if (solution.value?.architecture || solution.value?.architectureDesc || solution.value?.applications) {
    sections.push({ id: 'architecture', label: '技术架构' })
  }
  if (solution.value?.features?.length) {
    sections.push({ id: 'features', label: '核心功能' })
  }
  if (solution.value?.scenarios?.length) {
    sections.push({ id: 'scenarios', label: '应用场景' })
  }
  if (solution.value?.cases?.length) {
    sections.push({ id: 'cases', label: '客户案例' })
  }
  // 相关方案在侧边栏，但保留目录项
  sections.push({ id: 'related', label: '相关方案' })
  return sections
})

// ========== 获取详情 ==========
const fetchSolutionDetail = async (id) => {
  loading.value = true
  try {
    const response = await getSolutionById(id)
    const data = response.data
    if (data) {
      solution.value = {
        ...data,
        highlights: data.highlights || [],
        features: data.features || data.featureList || [],
        scenarios: data.scenarios || [],
        cases: data.cases || [],
        applications: data.applications || [],
        platforms: data.platforms || [],
        edgeNodes: data.edgeNodes || [],
        devices: data.devices || [],
        tags: data.tags || [],
        industryName: data.industryName || data.industry || '',
        content: data.content || data.fullDesc || data.desc || '',
        efficiency: data.efficiency || '',
        costReduction: data.costReduction || '',
        deploymentTime: data.deploymentTime || '',
      }
      await fetchRelatedSolutions(id)
    } else {
      solution.value = null
    }
  } catch (error) {
    console.error('加载方案详情失败:', error)
    ElMessage.error('加载方案详情失败')
    solution.value = null
  } finally {
    loading.value = false
  }
}

// ========== 获取相关方案 ==========
const fetchRelatedSolutions = async (excludeId) => {
  try {
    // 若后端有推荐接口，可调用。此处模拟示例数据
   
    
    // 临时模拟两条数据用于展示布局，实际请替换为真实接口
    relatedSolutions.value = [
      { id: '1', name: '智慧制造解决方案', industry: '制造', image: '' },
      { id: '2', name: '智慧城市大脑', industry: '政务', image: '' }
    ]
  } catch (error) {
    console.error('加载相关方案失败:', error)
    relatedSolutions.value = []
  }
}

// ========== 滚动到指定章节 ==========
const scrollToSection = (id) => {
  activeSection.value = id
  const el = document.getElementById(id)
  if (el) {
    const offset = 80
    const top = el.getBoundingClientRect().top + window.pageYOffset - offset
    window.scrollTo({ top, behavior: 'smooth' })
  }
}

// ========== 咨询按钮 ==========
const handleConsult = () => {
  ElMessage.success('已提交咨询，客服将尽快与您联系')
}

// ========== 跳转至方案详情 ==========
const goToDetail = (id) => {
  router.push(`/solution/${id}`)
}

// ========== 滚动监听（高亮当前目录项） ==========
const handleScroll = () => {
  const sections = navSections.value.map(s => document.getElementById(s.id)).filter(Boolean)
  let current = 'overview'
  for (const section of sections) {
    if (section) {
      const rect = section.getBoundingClientRect()
      if (rect.top <= 100) {
        current = section.id
      }
    }
  }
  activeSection.value = current
}

// ========== 生命周期 ==========
onMounted(() => {
  const id = route.params.id
  if (id) {
    fetchSolutionDetail(id)
  } else {
    loading.value = false
  }
  window.addEventListener('scroll', handleScroll)
})

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
})

watch(
  () => route.params.id,
  (newId) => {
    if (newId) {
      fetchSolutionDetail(newId)
    }
  }
)
</script>

<style scoped>
/* ========== 页面基础 ========== */
.solution-detail-page {
  padding-top: 72px;
  background: #f5f7fb;
  min-height: 100vh;
  font-family: -apple-system, 'SF Pro Display', 'PingFang SC', sans-serif;
}

/* ========== 加载状态 ========== */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  gap: 16px;
  color: #6b7280;
}
.loading-state .spin {
  color: #4F46E5;
}
.loading-state p {
  font-size: 16px;
}

/* ========== 404 ========== */
.not-found {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  gap: 16px;
  text-align: center;
  color: #6b7280;
}
.not-found .el-icon {
  color: #d1d5db;
}
.not-found h2 {
  font-size: 24px;
  color: #1a1a2e;
  margin: 0;
}
.not-found p {
  margin: 0 0 8px 0;
}

/* ========== 详情英雄区 ========== */
.detail-hero {
  position: relative;
  min-height: 480px;
  display: flex;
  align-items: center;
  overflow: hidden;
}
.hero-backdrop {
  position: absolute;
  inset: 0;
  z-index: 0;
}
.hero-bg-image {
  width: 100%;
  height: 100%;
  display: block;
}
.hero-bg-fallback {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #0a0a1a 0%, #1a1a3e 100%);
}
.hero-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(10, 10, 26, 0.85) 0%, rgba(10, 10, 26, 0.4) 100%);
}
.hero-content {
  position: relative;
  z-index: 1;
  max-width: 1440px;
  margin: 0 auto;
  padding: 60px 64px;
  width: 100%;
  color: #fff;
}
.hero-breadcrumb {
  margin-bottom: 32px;
}
.hero-breadcrumb :deep(.el-breadcrumb__item) {
  color: rgba(255, 255, 255, 0.6);
}
.hero-breadcrumb :deep(.el-breadcrumb__item .el-breadcrumb__inner) {
  color: rgba(255, 255, 255, 0.6);
}
.hero-breadcrumb :deep(.el-breadcrumb__item:last-child .el-breadcrumb__inner) {
  color: #fff;
}
.hero-breadcrumb :deep(.el-breadcrumb__separator) {
  color: rgba(255, 255, 255, 0.3);
}
.hero-main {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 48px;
}
.hero-info {
  flex: 1;
}
.hero-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}
.hero-tag {
  padding: 4px 14px;
  border-radius: 100px;
  font-size: 12px;
  font-weight: 500;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.06);
}
.hero-tag.primary {
  background: #4F46E5;
  color: #fff;
  border-color: #4F46E5;
}
.hero-title {
  font-size: 44px;
  font-weight: 700;
  line-height: 1.1;
  margin: 0 0 16px 0;
  letter-spacing: -1px;
}
.hero-desc {
  font-size: 18px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.7;
  max-width: 600px;
  margin: 0 0 24px 0;
}
.hero-meta {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 28px;
}
.meta-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
}
.meta-item .el-icon {
  font-size: 16px;
}
.meta-divider {
  width: 1px;
  height: 18px;
  background: rgba(255, 255, 255, 0.1);
}
.hero-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}
.action-btn {
  background: #fff !important;
  color: #0a0a1a !important;
  border: none !important;
  padding: 12px 36px !important;
  font-weight: 600 !important;
  transition: all 0.3s ease;
}
.action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 32px rgba(255, 255, 255, 0.2) !important;
}
.action-btn-ghost {
  border: 1.5px solid rgba(255, 255, 255, 0.2) !important;
  background: transparent !important;
  color: #fff !important;
  padding: 12px 36px !important;
  font-weight: 500 !important;
  transition: all 0.3s ease;
}
.action-btn-ghost:hover {
  border-color: rgba(255, 255, 255, 0.5) !important;
  background: rgba(255, 255, 255, 0.05) !important;
}
.hero-stats-card {
  flex: 0 0 240px;
  background: rgba(255, 255, 255, 0.06);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 28px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.hero-stats-card .stat-item {
  display: flex;
  flex-direction: column;
}
.hero-stats-card .stat-number {
  font-size: 28px;
  font-weight: 700;
  color: #fff;
}
.hero-stats-card .stat-label {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.4);
  margin-top: 2px;
}
.hero-stats-card .stat-divider {
  width: 100%;
  height: 1px;
  background: rgba(255, 255, 255, 0.06);
}

/* ========== 详情主体 ========== */
.detail-body {
  max-width: 80%;
  margin: 0 auto;
  padding: 40px 64px 80px;
}
.body-inner {
  background: #ffffff;
  border-radius: 24px;
  padding: 40px 44px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.04);
}
.body-layout {
  display: flex;
  gap: 48px;
  align-items: flex-start;
}

/* ====== 侧边导航 ====== */
.detail-nav {
  flex: 0 0 200px;
  position: sticky;
  top: 92px;
}
.nav-sticky {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.nav-title {
  font-size: 13px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}
.nav-list {
  list-style: none;
  padding: 0;
  margin: 0 0 16px 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.nav-link {
  display: block;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 14px;
  color: #6b7280;
  text-decoration: none;
  transition: all 0.2s ease;
  cursor: pointer;
}
.nav-link:hover {
  background: #f3f4f6;
  color: #1a1a2e;
}
.nav-link.active {
  background: rgba(79, 70, 229, 0.08);
  color: #4F46E5;
  font-weight: 500;
}
.nav-cta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 10px;
  border: none;
  background: #0a0a1a;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;
  margin-top: 8px;
}
.nav-cta:hover {
  background: #2a2a4a;
  transform: translateY(-2px);
}

/* ====== 内容区左右布局 ====== */
.detail-content {
  flex: 1;
  min-width: 0;
}
.content-wrapper {
  display: flex;
  gap: 40px;
  align-items: flex-start;
}
.main-content {
  flex: 1;
  min-width: 0;
}
.related-sidebar {
  flex: 0 0 280px;
  position: sticky;
  top: 92px;
  align-self: flex-start;
}

.content-section {
  margin-bottom: 56px;
  scroll-margin-top: 80px;
}
.content-section:last-child {
  margin-bottom: 0;
}
.section-title {
  font-size: 24px;
  font-weight: 700;
  color: #0a0a1a;
  margin: 0 0 20px 0;
  padding-bottom: 12px;
  border-bottom: 2px solid #f0f2f5;
  position: relative;
}
.section-title::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 60px;
  height: 2px;
  background: #4F46E5;
  border-radius: 2px;
}
.section-body {
  padding-top: 4px;
}
.section-sub-desc {
  font-size: 15px;
  color: #6b7280;
  line-height: 1.7;
  margin: 0 0 24px 0;
}

/* ---- Markdown 预览 ---- */
.markdown-preview {
  margin-bottom: 28px;
}
.markdown-preview :deep(.v-note-wrapper) {
  border: none !important;
  box-shadow: none !important;
  background: transparent !important;
}
.markdown-preview :deep(.v-note-wrapper .v-note-panel) {
  border: none !important;
}
.markdown-preview :deep(.v-note-wrapper .v-note-panel .v-note-show) {
  padding: 0 !important;
}
.architecture-wrapper {
  margin-top: 8px;
}
.architecture-wrapper :deep(.v-note-wrapper) {
  border: none !important;
  box-shadow: none !important;
  background: transparent !important;
}

/* ---- 概述亮点 ---- */
.overview-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
  margin-top: 8px;
}
.overview-card {
  display: flex;
  gap: 14px;
  padding: 18px 20px;
  background: #f8f9fc;
  border-radius: 12px;
  border: 1px solid #f0f2f5;
  transition: all 0.3s ease;
}
.overview-card:hover {
  border-color: #4F46E5;
  box-shadow: 0 4px 16px rgba(79, 70, 229, 0.06);
}
.ov-icon {
  flex: 0 0 36px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(79, 70, 229, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4F46E5;
}
.ov-content h4 {
  font-size: 15px;
  font-weight: 600;
  color: #0a0a1a;
  margin: 0 0 4px 0;
}
.ov-content p {
  font-size: 13px;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
}

/* ---- 架构图（兼容旧结构） ---- */
.architecture-diagram {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 28px 20px;
  background: #f8f9fc;
  border-radius: 16px;
  border: 1px solid #f0f2f5;
}
.arch-layer {
  width: 100%;
  max-width: 600px;
  padding: 14px 20px;
  border-radius: 12px;
  text-align: center;
  transition: all 0.3s ease;
}
.arch-layer:hover {
  transform: translateY(-2px);
}
.arch-layer-1 {
  background: rgba(79, 70, 229, 0.08);
  border: 1px solid rgba(79, 70, 229, 0.12);
}
.arch-layer-2 {
  background: rgba(16, 185, 129, 0.08);
  border: 1px solid rgba(16, 185, 129, 0.12);
}
.arch-layer-3 {
  background: rgba(245, 158, 11, 0.08);
  border: 1px solid rgba(245, 158, 11, 0.12);
}
.arch-layer-4 {
  background: rgba(239, 68, 68, 0.08);
  border: 1px solid rgba(239, 68, 68, 0.12);
}
.layer-label {
  display: inline-block;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}
.arch-layer-1 .layer-label { color: #4F46E5; }
.arch-layer-2 .layer-label { color: #10b981; }
.arch-layer-3 .layer-label { color: #f59e0b; }
.arch-layer-4 .layer-label { color: #ef4444; }
.layer-items {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
}
.layer-item {
  padding: 4px 14px;
  border-radius: 6px;
  font-size: 13px;
  background: rgba(255, 255, 255, 0.8);
  color: #1a1a2e;
  border: 1px solid rgba(0, 0, 0, 0.04);
}
.arch-arrow {
  font-size: 20px;
  color: #d1d5db;
  padding: 2px 0;
}

/* ---- 核心功能 ---- */
.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 20px;
}
.feature-card {
  padding: 24px 20px;
  border-radius: 14px;
  border: 1px solid #f0f2f5;
  background: #fff;
  transition: all 0.3s ease;
  text-align: center;
}
.feature-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06);
  border-color: rgba(79, 70, 229, 0.15);
}
.feature-icon {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 14px;
  color: #fff;
}
.feature-card h4 {
  font-size: 15px;
  font-weight: 600;
  color: #0a0a1a;
  margin: 0 0 6px 0;
}
.feature-card p {
  font-size: 13px;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
}

/* ---- 应用场景 ---- */
.scenarios-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 24px;
}
.scenario-card {
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid #f0f2f5;
  transition: all 0.3s ease;
  background: #fff;
}
.scenario-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.06);
}
.scenario-image {
  width: 100%;
  padding-top: 56%;
  position: relative;
  overflow: hidden;
  background: #f5f7fb;
}
.scenario-image .el-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
.scenario-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #eef2f6;
  color: #b0b8c4;
}
.scenario-info {
  padding: 18px 20px 20px;
}
.scenario-info h4 {
  font-size: 15px;
  font-weight: 600;
  color: #0a0a1a;
  margin: 0 0 4px 0;
}
.scenario-info p {
  font-size: 13px;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
}

/* ---- 客户案例 ---- */
.cases-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
}
.case-card {
  display: flex;
  gap: 16px;
  padding: 20px;
  border-radius: 14px;
  border: 1px solid #f0f2f5;
  background: #fafbfc;
  transition: all 0.3s ease;
}
.case-card:hover {
  border-color: #4F46E5;
}
.case-avatar {
  flex: 0 0 56px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  overflow: hidden;
  background: #eef2f6;
}
.case-avatar .el-image {
  width: 100%;
  height: 100%;
}
.avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #eef2f6;
  color: #b0b8c4;
}
.case-info h4 {
  font-size: 15px;
  font-weight: 600;
  color: #0a0a1a;
  margin: 0 0 4px 0;
}
.case-info p {
  font-size: 13px;
  color: #6b7280;
  margin: 0 0 6px 0;
  line-height: 1.5;
}
.case-result {
  display: inline-block;
  font-size: 12px;
  font-weight: 600;
  color: #10b981;
  background: rgba(16, 185, 129, 0.08);
  padding: 2px 12px;
  border-radius: 100px;
}

/* ---- 相关方案（侧边栏） ---- */
.related-sidebar .related-grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.related-sidebar .related-card {
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #f0f2f5;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #fff;
}
.related-sidebar .related-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
  border-color: #4F46E5;
}
.related-sidebar .related-image {
  width: 100%;
  padding-top: 56%;
  position: relative;
  background: #f5f7fb;
}
.related-sidebar .related-image .el-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
.related-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #eef2f6;
  color: #b0b8c4;
}
.related-sidebar .related-info {
  padding: 14px 16px 16px;
}
.related-sidebar .related-info h4 {
  font-size: 14px;
  font-weight: 600;
  color: #0a0a1a;
  margin: 0 0 4px 0;
}
.related-sidebar .related-info span {
  font-size: 12px;
  color: #9ca3af;
}

/* ========== 响应式 ========== */
@media (max-width: 1200px) {
  .hero-content { padding: 48px 48px; }
  .hero-title { font-size: 36px; }
  .hero-stats-card { flex: 0 0 200px; padding: 20px 18px; }
  .hero-stats-card .stat-number { font-size: 22px; }
  .detail-body { padding: 32px 40px 60px; }
  .body-inner { padding: 32px 32px; }
  .body-layout { gap: 32px; }
}
@media (max-width: 1024px) {
  .hero-main { flex-direction: column; }
  .hero-stats-card {
    flex: none;
    width: 100%;
    flex-direction: row;
    justify-content: space-around;
    padding: 20px;
  }
  .hero-stats-card .stat-divider {
    width: 1px;
    height: auto;
  }
  .detail-nav { flex: 0 0 160px; }
  .detail-body { padding: 24px 32px 48px; }
  .body-inner { padding: 28px 24px; }
  .body-layout { gap: 24px; }
  .content-wrapper {
    flex-direction: column;
    gap: 32px;
  }
  .related-sidebar {
    flex: none;
    width: 100%;
    position: static;
  }
  .related-sidebar .related-grid {
    flex-direction: row;
    flex-wrap: wrap;
  }
  .related-sidebar .related-card {
    flex: 1 1 200px;
  }
}
@media (max-width: 820px) {
  .hero-content { padding: 40px 24px; }
  .hero-title { font-size: 30px; }
  .hero-desc { font-size: 16px; }
  .detail-body { padding: 16px 16px 40px; }
  .body-inner { padding: 20px 16px; border-radius: 16px; }
  .body-layout { flex-direction: column; gap: 20px; }
  .detail-nav {
    flex: none;
    width: 100%;
    position: static;
  }
  .nav-sticky {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 12px;
    padding-bottom: 16px;
    border-bottom: 1px solid #f0f2f5;
  }
  .nav-title { margin-bottom: 0; font-size: 12px; }
  .nav-list {
    flex-direction: row;
    flex-wrap: wrap;
    margin-bottom: 0;
    gap: 4px;
  }
  .nav-link { padding: 4px 10px; font-size: 13px; }
  .nav-cta { margin-top: 0; padding: 6px 16px; font-size: 13px; }
  .section-title { font-size: 20px; }
  .overview-grid { grid-template-columns: 1fr 1fr; }
  .features-grid { grid-template-columns: 1fr 1fr; }
  .scenarios-grid { grid-template-columns: 1fr; }
  .cases-grid { grid-template-columns: 1fr; }
  .related-sidebar .related-grid {
    flex-direction: column;
  }
  .related-sidebar .related-card {
    flex: none;
  }
}
@media (max-width: 480px) {
  .hero-content { padding: 28px 16px; }
  .hero-title { font-size: 24px; }
  .hero-desc { font-size: 14px; }
  .hero-actions { flex-direction: column; }
  .hero-actions .el-button { width: 100%; justify-content: center; }
  .hero-stats-card { flex-wrap: wrap; gap: 12px; }
  .hero-stats-card .stat-divider { display: none; }
  .detail-body { padding: 12px 12px 32px; }
  .body-inner { padding: 16px 12px; }
  .overview-grid { grid-template-columns: 1fr; }
  .features-grid { grid-template-columns: 1fr; }
  .related-sidebar .related-grid { flex-direction: column; }
  .section-title { font-size: 18px; }
  .architecture-diagram { padding: 16px 12px; }
  .layer-items .layer-item { font-size: 12px; padding: 3px 10px; }
}
</style>